import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ButtonContent } from '../ui/ButtonContent';
import { useSupabase } from '../../lib/SupabaseProvider';
import type { InterviewSession } from '../../types';
import { Skeleton } from '../ui/Skeleton';
import { IconAlignLeft, IconRefresh } from '@tabler/icons-react';
import { SuccessIcon } from '@/components/icons';
import { AggregatedReport } from './report/AggregatedReport';
import { CAMPAIGN_REPORT_VERSION, type AggregatedReportData } from './report/AggregatedReport';

/** Props for {@link CampaignReport}. */
export interface CampaignReportProps {
  /** The interview session. */
  session: InterviewSession;
  /** Number of completed responses. */
  completedCount: number;
  /** Number of new responses since the last campaign report was generated. */
  newResponsesSinceReport: number;
  /** Whether report generation is in progress. */
  generatingReport: boolean;
  /** Error message from the last generation attempt. */
  reportError: string | null;
  /** Whether the current user may generate or refresh reports. */
  canGenerate: boolean;
  /** Callback to trigger report generation / regeneration. */
  onGenerate: () => void;
  /** Callback fired when report content is received from generation. */
  onReportGenerated?: (content: string) => void;
}

/**
 * Renders a lightweight markdown-to-HTML conversion for legacy campaign reports.
 * Safe because content comes from our own Gemini pipeline, not user input.
 */
const renderMarkdown = (content: string): string =>
  content
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold text-foreground mt-4 mb-2">$1</h3>')
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc text-muted-foreground">$1</li>')
    .replace(/\n\n/g, '<br /><br />');

/** What the stored campaign report holds once loaded. */
interface StoredCampaignReport {
  /** True until the first fetch settles. */
  loading: boolean;
  /** The structured report, when the stored content is one. */
  aggregatedData: AggregatedReportData | null;
  /** Legacy markdown content, when the stored content is not structured. */
  legacyMarkdown: string | null;
}

/** Parse stored content into a structured report or legacy markdown. */
function parseStoredReport(content: string): Pick<StoredCampaignReport, 'aggregatedData' | 'legacyMarkdown'> {
  try {
    const parsed = JSON.parse(content);
    // Valid JSON but not the aggregated format: show it as text.
    return parsed.meta?.respondentCount ? { aggregatedData: parsed as AggregatedReportData, legacyMarkdown: null } : { aggregatedData: null, legacyMarkdown: content };
  } catch {
    // Not JSON: legacy markdown content.
    return { aggregatedData: null, legacyMarkdown: content };
  }
}

/**
 * Load the stored campaign report. Stale-while-revalidate: only block on a
 * true cold load; when switching campaigns the previous report stays until
 * the new one resolves, then swaps in one pass. A newer `generatedAt` (set when
 * a rebuild finishes) fetches again. The raw content is kept so an unchanged
 * report never re-renders.
 */
function useStoredCampaignReport(sessionId: string, generatedAt: string | null | undefined): StoredCampaignReport {
  const { supabase } = useSupabase();
  const [stored, setStored] = useState<{ loading: boolean; content: string | null }>({ loading: true, content: null });

  useEffect(() => {
    let cancelled = false;
    /** Settle on content, keeping the same state object when nothing changed. */
    const settle = (content: string | null) => setStored(previous => (!previous.loading && previous.content === content ? previous : { loading: false, content }));

    async function fetchReport() {
      try {
        const { data } = await supabase
          .from('case_studies_visible')
          .select('content')
          .eq('interview_session_id', sessionId)
          .eq('content_type', 'campaign_report')
          .is('interview_data_id', null)
          .maybeSingle();
        if (cancelled) return;
        // No content yet, or a generation-lock placeholder: clear any stale
        // report from the previous campaign so the empty/generating state shows.
        settle(!data?.content || data.content.startsWith('__GEN_IN_PROGRESS__:') ? null : data.content);
      } catch {
        // Network error: keep prior content on screen rather than blanking it.
        if (!cancelled) setStored(previous => (previous.loading ? { ...previous, loading: false } : previous));
      }
    }

    void fetchReport();
    return () => { cancelled = true; };
  }, [sessionId, generatedAt, supabase]);

  const parsed = useMemo(() => (stored.content ? parseStoredReport(stored.content) : { aggregatedData: null, legacyMarkdown: null }), [stored.content]);
  return { loading: stored.loading, ...parsed };
}

/** "3h ago" style label for when the report was last generated. */
function relativeLabel(timestamp: string | null | undefined): string | null {
  if (!timestamp) return null;
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (diffMs < 60_000) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** "Last updated" with the refresh control (or plain text without permission). */
function LastUpdated({ label, canGenerate, generatingReport, newResponsesSinceReport, onGenerate }: {
  label: string | null;
} & Pick<CampaignReportProps, 'canGenerate' | 'generatingReport' | 'newResponsesSinceReport' | 'onGenerate'>) {
  const [upToDate, setUpToDate] = useState(false);
  const text = `Last updated ${label || 'recently'}`;
  if (!canGenerate) return <span className="text-xs text-muted-foreground">{text}</span>;

  /** Re-aggregate when there are new responses, otherwise confirm it is current. */
  const refresh = () => {
    if (newResponsesSinceReport > 0) {
      onGenerate();
    } else {
      setUpToDate(true);
      setTimeout(() => setUpToDate(false), 3000);
    }
  };

  return (
    <button type="button" onClick={refresh} disabled={generatingReport} aria-busy={generatingReport}
      className="group flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:cursor-default disabled:hover:text-muted-foreground">
      <ButtonContent loading={generatingReport}>{upToDate ? (
        <>
          <SuccessIcon className="h-3.5 w-3.5 text-success" />
          <span className="text-success">Report is up to date!</span>
        </>
      ) : (
        <>
          <IconRefresh className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180 motion-reduce:transition-none" />
          <span>{text}</span>
        </>
      )}</ButtonContent>
    </button>
  );
}

/** Card chrome shared with the Ask hollie suggestions it sits beside. */
const SUMMARY_CARD = 'flex flex-col gap-4 rounded-[20px] border border-border/50 bg-surface px-6 py-5 light:border-transparent';

/** The pieces of the campaign report, placed separately by the Overview. */
export interface CampaignReportPartsValue {
  /** "Last updated" with the refresh control; null until a report exists. */
  updated: React.ReactNode;
  /** Summary card, shown beside the Ask hollie suggestions; null when there is nothing to summarize. */
  summary: React.ReactNode;
  /** The written sections and any legacy report, shown after the highlights. */
  body: React.ReactNode;
}

/**
 * Loads the campaign report and hands its last-updated control, summary card
 * and written sections to `children`, so they read as one report with the
 * deterministic Overview.
 */
export const CampaignReportParts: React.FC<CampaignReportProps & {
  children: (parts: CampaignReportPartsValue) => React.ReactNode;
  /** Called once per campaign when the stored report was built by an older pipeline and may be rebuilt. */
  onOutdated?: () => void;
}> = ({
  session,
  newResponsesSinceReport,
  generatingReport,
  reportError,
  canGenerate,
  onGenerate,
  children,
  onOutdated,
}) => {
  const { loading, aggregatedData, legacyMarkdown } = useStoredCampaignReport(session.id, session.aggregated_report_generated_at);
  const outdated = !loading && ((aggregatedData && aggregatedData.meta.reportVersion !== CAMPAIGN_REPORT_VERSION) || Boolean(legacyMarkdown));
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!outdated || !canGenerate || generatingReport || askedFor.current === session.id) return;
    askedFor.current = session.id;
    onOutdated?.();
  }, [outdated, canGenerate, generatingReport, session.id, onOutdated]);
  const updated = (
    <LastUpdated
      label={relativeLabel(aggregatedData?.meta?.generatedAt || session.aggregated_report_generated_at)}
      canGenerate={canGenerate}
      generatingReport={generatingReport}
      newResponsesSinceReport={newResponsesSinceReport}
      onGenerate={onGenerate}
    />
  );
  const error = reportError && <p className="text-sm text-danger-soft">{reportError}</p>;
  const heading = (
    <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
      <IconAlignLeft aria-hidden="true" className="h-5 w-5 text-accent-soft" />
      Summary
    </h2>
  );

  let summary: React.ReactNode = null;
  if (loading || aggregatedData || !legacyMarkdown) {
    const pending = loading || (!aggregatedData && generatingReport);
    summary = (
      <section aria-label="Summary" aria-busy={pending || undefined} className={SUMMARY_CARD}>
        {heading}
        {aggregatedData ? (
          <p className="text-sm leading-relaxed text-foreground">{aggregatedData.tldr}</p>
        ) : pending ? (
          <p role="status" aria-label={generatingReport ? 'Generating summary' : 'Loading summary'} className="text-sm leading-relaxed text-foreground">
            <Skeleton count={3} />
          </p>
        ) : (
          <div className="flex flex-1 items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>Not ready yet</span>
            {canGenerate && (
              <button type="button" onClick={onGenerate} className="rounded-full px-3 py-1 text-xs text-foreground hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-ring">
                Refresh
              </button>
            )}
          </div>
        )}
        {error}
        {(aggregatedData || pending) && <p className="mt-auto text-xs text-muted-foreground">
          {aggregatedData ? `Based on ${aggregatedData.meta.respondentCount} conversation${aggregatedData.meta.respondentCount === 1 ? '' : 's'}` : <Skeleton width="10rem" />}
        </p>}
      </section>
    );
  }

  let body: React.ReactNode = null;
  if (!loading && aggregatedData) {
    body = <AggregatedReport data={aggregatedData} />;
  } else if (!loading && legacyMarkdown) {
    body = (
      <div className="flex flex-col gap-3 rounded-[20px] border border-border/50 bg-surface p-6 light:border-transparent">
        {error}
        <div className="prose dark:prose-invert prose-sm max-w-none">
          <div
            className="text-sm text-foreground leading-relaxed whitespace-pre-wrap [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-foreground [&_h2]:mt-6 [&_h2]:mb-3 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_strong]:text-foreground [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(legacyMarkdown) }}
          />
        </div>
      </div>
    );
  }

  return <>{children({ updated: !loading && (aggregatedData || legacyMarkdown) ? updated : null, summary, body })}</>;
};

/**
 * The campaign report on its own (last updated, then the summary card above
 * the written sections), for places without the Overview.
 */
export const CampaignReport: React.FC<CampaignReportProps> = (props) => (
  <CampaignReportParts {...props}>
    {({ updated, summary, body }) => (
      <div className="flex flex-col gap-6">
        {updated && <div className="flex justify-end">{updated}</div>}
        {summary}
        {body}
      </div>
    )}
  </CampaignReportParts>
);
